// src/FilePreview.js
// Окно предпросмотра офисного документа. Сервер конвертирует файл в PDF
// у себя, поэтому документ не покидает школьный сервер.

import React, { useEffect, useState } from 'react';
import { api } from './api';
import FileTypeIcon from './FileTypeIcon';
import './FilePreview.css';

export default function FilePreview({ attachment, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  const fileName = attachment.display_name || attachment.file_name || 'Файл';
  const downloadUrl = attachment.file_url || attachment.file;

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;

    (async () => {
      try {
        const blob = await api.getAttachmentPreview(attachment.id);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Не удалось открыть предпросмотр');
      }
    })();

    return () => {
      cancelled = true;
      // Освобождаем память: без этого blob висит до перезагрузки страницы
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.id]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="file-preview" onClick={onClose}>
      <div
        className="file-preview-window"
        role="dialog"
        aria-modal="true"
        aria-label={`Предпросмотр: ${fileName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="file-preview-header">
          <div className="file-preview-name">
            <FileTypeIcon
              fileName={attachment.file_name || attachment.file || fileName}
              fileType={attachment.file_type}
              size={26}
            />
            <span title={fileName}>{fileName}</span>
          </div>

          <div className="file-preview-actions">
            <a
              className="file-preview-button primary"
              href={downloadUrl}
              download={fileName}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Скачать
            </a>

            <button type="button" className="file-preview-close" onClick={onClose} aria-label="Закрыть">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </header>

        <div className="file-preview-body">
          {error ? (
            <div className="file-preview-state">
              <h3>Не удалось показать документ</h3>
              <p>{error}</p>
              <a className="file-preview-button primary" href={downloadUrl} download={fileName}>
                Скачать файл
              </a>
            </div>
          ) : !blobUrl ? (
            <div className="file-preview-state">
              <div className="file-preview-spinner"></div>
              <p>Готовим предпросмотр...</p>
            </div>
          ) : (
            <iframe
              className="file-preview-frame"
              // #toolbar=0 убирает панель встроенного просмотрщика PDF
              // вместе с кнопкой скачивания: сохранять нужно исходный
              // документ кнопкой «Скачать», а не служебный PDF.
              src={`${blobUrl}#toolbar=0&navpanes=0`}
              title={`Предпросмотр: ${fileName}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
